import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import axios from "axios";

export default function Add_data_modal({
  select_data,
  open_update_data_modal,
  onUpdateAsset,
}) {
  const [showModal, setShowModal] = useState(false);

  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [hubs, setHubs] = useState([]);

  const [formData, setFormData] = useState({
    _id: "",
    assetname: "",
    company: "",
    id_assettype: "",
    id_user: "",
    assignFromDate: "",
    assignToDate: "",
    vehicle: "",
    id_hub: "",
    approved: false,
    approvedBy: "",
    quantity: 1,
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ============================================================
  // SELECT STYLES
  // ============================================================

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      borderColor: "#ced4da",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
  };

  // ============================================================
  // OPEN / CLOSE MODAL
  // ============================================================

  useEffect(() => {
    setShowModal(Boolean(open_update_data_modal?.modal_open));
  }, [open_update_data_modal]);

  const handleClose = () => {
    if (loading) return;

    setShowModal(false);
  };

  // ============================================================
  // HELPERS
  // ============================================================

  const formatDateForInput = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toISOString().split("T")[0];
  };

  // ============================================================
  // FETCH DROPDOWN DATA
  // ============================================================

  useEffect(() => {
    const fetchDropdownData = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found");
        return;
      }

      try {
        const [
          usersResponse,
          assetTypesResponse,
          vehiclesResponse,
          hubsResponse,
        ] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/admin/users`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),

          axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/asset-types`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),

          axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/vehicles`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),

          axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/hubs`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        setUsers(usersResponse.data.users || []);

        const assetTypeGroups = assetTypesResponse.data.assetTypes || [];

        const assetTypeEntries = assetTypeGroups.flatMap(
          (group) => group.entries || [],
        );

        setAssets(assetTypeEntries);

        setVehicles(
          vehiclesResponse.data.vehicles || vehiclesResponse.data.data || [],
        );

        setHubs(hubsResponse.data.hubs || hubsResponse.data.data || []);
      } catch (error) {
        console.error(
          "Error fetching asset update dropdown data:",
          error.response?.data || error.message,
        );

        setUsers([]);
        setAssets([]);
        setVehicles([]);
        setHubs([]);
      }
    };

    fetchDropdownData();
  }, []);

  // ============================================================
  // INITIALIZE FORM FROM SELECTED ASSET
  // ============================================================

  useEffect(() => {
    if (!select_data) return;

    const assetTypeId =
      select_data?.id_assettype || select_data?.assetType?._id || "";

    const userId = select_data?.id_user || select_data?.user?._id || "";

    const vehicleId =
      select_data?.vehicle || select_data?.vehicleData?._id || "";

    const hubId = select_data?.id_hub || select_data?.hubData?._id || "";

    const approvedById =
      select_data?.approvedBy || select_data?.approvedByData?._id || "";

    setFormData({
      _id: select_data?._id || "",

      assetname: select_data?.assetType?.assetname || "",

      company: select_data?.company || "",

      id_assettype: assetTypeId,

      id_user: userId,

      assignFromDate: formatDateForInput(select_data?.assignFromDate),

      assignToDate: formatDateForInput(select_data?.assignToDate),

      vehicle: vehicleId,

      id_hub: hubId,

      approved: Boolean(select_data?.approved),

      approvedBy: approvedById,

      quantity: Number(select_data?.quantity || 1),

      description: select_data?.description || "",
    });

    setErrors({});
  }, [select_data]);

  // ============================================================
  // ASSET TYPE OPTIONS
  // ============================================================

  const assetTypeOptions = [
    ...new Map(
      assets
        .filter((asset) => asset?.assetname)
        .map((asset) => [
          asset.assetname,
          {
            value: asset.assetname,
            label: asset.assetname,
          },
        ]),
    ).values(),
  ];

  // ============================================================
  // SELECTED ASSET TYPE RECORDS
  // ============================================================

  const selectedAssetRecords = assets.filter(
    (asset) => asset?.assetname === formData.assetname,
  );

  // ============================================================
  // COMPANY OPTIONS
  // ============================================================

  const companyOptions = [
    ...new Map(
      selectedAssetRecords
        .filter((asset) => asset?.company)
        .map((asset) => [
          asset.company,
          {
            value: asset.company,
            label: asset.company,
          },
        ]),
    ).values(),
  ];

  // ============================================================
  // USER OPTIONS
  // ============================================================

  const userOptions = users.map((user) => ({
    value: user._id,
    label: `${user.name || "Unknown Name"}${
      user.phonenumber ? ` (${user.phonenumber})` : ""
    }`,
  }));

  // ============================================================
  // VEHICLE OPTIONS
  // ============================================================

  const vehicleOptions = vehicles.map((vehicle) => ({
    value: vehicle._id,
    label:
      vehicle.numberplate ||
      vehicle.vehicleNumber ||
      vehicle.name ||
      "Unknown Vehicle",
  }));

  // ============================================================
  // HUB OPTIONS
  // ============================================================

  const hubOptions = hubs.map((hub) => ({
    value: hub._id,
    label: hub.name || hub.hubname || hub.hubName || "Unknown Hub",
  }));

  // ============================================================
  // APPROVED BY OPTIONS
  // ============================================================

  const approvedByOptions = users.map((user) => ({
    value: user._id,
    label: `${user.name || "Unknown Name"}${
      user.phonenumber ? ` (${user.phonenumber})` : ""
    }`,
  }));

  // ============================================================
  // ASSET TYPE CHANGE
  // ============================================================

  const handleAssetTypeChange = (selectedOption) => {
    const assetname = selectedOption ? selectedOption.value : "";

    setFormData((prev) => ({
      ...prev,
      assetname,
      company: "",
      id_assettype: "",
    }));

    setErrors((prev) => ({
      ...prev,
      assetname: "",
      company: "",
      id_assettype: "",
    }));
  };

  // ============================================================
  // COMPANY CHANGE
  // ============================================================

  const handleCompanyChange = (selectedOption) => {
    const company = selectedOption ? selectedOption.value : "";

    const selectedAsset = assets.find(
      (asset) =>
        asset?.assetname === formData.assetname && asset?.company === company,
    );

    setFormData((prev) => ({
      ...prev,
      company,
      id_assettype: selectedAsset?._id || "",
    }));

    setErrors((prev) => ({
      ...prev,
      company: "",
      id_assettype: "",
    }));
  };

  // ============================================================
  // GENERIC SELECT CHANGE
  // ============================================================

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ============================================================
  // INPUT CHANGE
  // ============================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ============================================================
  // APPROVED CHANGE
  // ============================================================

  const handleApprovedChange = (event) => {
    const approved = event.target.checked;

    setFormData((prev) => ({
      ...prev,
      approved,
      approvedBy: approved ? prev.approvedBy : "",
    }));

    setErrors((prev) => ({
      ...prev,
      approvedBy: "",
    }));
  };

  // ============================================================
  // VALIDATION
  // ============================================================

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id_assettype) {
      newErrors.id_assettype = "Asset Type and Company are required.";
    }

    if (!formData.id_user) {
      newErrors.id_user = "User is required.";
    }

    if (!formData.assignFromDate) {
      newErrors.assignFromDate = "Assign From date is required.";
    }

    if (
      formData.assignToDate &&
      formData.assignFromDate &&
      new Date(formData.assignToDate) < new Date(formData.assignFromDate)
    ) {
      newErrors.assignToDate =
        "Assign To date cannot be before Assign From date.";
    }

    if (!formData.quantity || Number(formData.quantity) <= 0) {
      newErrors.quantity = "Quantity must be greater than 0.";
    }

    if (formData.approved && !formData.approvedBy) {
      newErrors.approvedBy = "Approved By is required when asset is approved.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ============================================================
  // UPDATE
  // ============================================================

  const handleUpdate = async () => {
    if (!validateForm()) {
      Swal.fire("Please fill in all required fields", "", "error");
      return;
    }

    if (!formData._id) {
      Swal.fire("Error", "Asset ID is missing.", "error");
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Update Asset?",
      text: "Are you sure you want to update this asset?",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        _id: formData._id,

        id_assettype: formData.id_assettype,

        id_user: formData.id_user,

        assignFromDate: formData.assignFromDate,

        assignToDate: formData.assignToDate || null,

        vehicle: formData.vehicle || null,

        id_hub: formData.id_hub || null,

        approved: Boolean(formData.approved),

        approvedBy: formData.approved ? formData.approvedBy || null : null,

        company: formData.company || "",

        quantity: Number(formData.quantity),

        description: formData.description || "",
      };

      /*
       * Parent component handles the API call:
       * PUT /api/v1/assets/:id
       */
      await onUpdateAsset(payload);

      setShowModal(false);
    } catch (error) {
      console.error(
        "Error updating asset:",
        error.response?.data || error.message,
      );

      Swal.fire(
        "Error!",
        error.response?.data?.message || "Asset details could not be updated.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <Modal show={showModal} onHide={handleClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <b>Update Asset</b>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row">
          {/* =====================================================
              ASSET TYPE
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Asset Type *</b>
            </label>

            <Select
              name="assetname"
              options={assetTypeOptions}
              value={
                assetTypeOptions.find(
                  (option) => option.value === formData.assetname,
                ) || null
              }
              onChange={handleAssetTypeChange}
              placeholder="Select Asset Type"
              isClearable
              styles={selectStyles}
            />

            {errors.assetname && (
              <div className="text-danger mt-1">{errors.assetname}</div>
            )}
          </div>

          {/* =====================================================
              COMPANY
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Company *</b>
            </label>

            <Select
              name="company"
              options={companyOptions}
              value={
                companyOptions.find(
                  (option) => option.value === formData.company,
                ) || null
              }
              onChange={handleCompanyChange}
              placeholder={
                formData.assetname
                  ? "Select Company"
                  : "Select Asset Type first"
              }
              isClearable
              isDisabled={!formData.assetname}
              styles={selectStyles}
            />

            {errors.company && (
              <div className="text-danger mt-1">{errors.company}</div>
            )}

            {errors.id_assettype && (
              <div className="text-danger mt-1">{errors.id_assettype}</div>
            )}
          </div>

          {/* =====================================================
              USER
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>User *</b>
            </label>

            <Select
              name="id_user"
              options={userOptions}
              value={
                userOptions.find(
                  (option) => option.value === formData.id_user,
                ) || null
              }
              onChange={handleSelectChange}
              placeholder="Select User"
              isClearable
              styles={selectStyles}
            />

            {errors.id_user && (
              <div className="text-danger mt-1">{errors.id_user}</div>
            )}
          </div>

          {/* =====================================================
              VEHICLE
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Vehicle</b>
            </label>

            <Select
              name="vehicle"
              options={vehicleOptions}
              value={
                vehicleOptions.find(
                  (option) => option.value === formData.vehicle,
                ) || null
              }
              onChange={handleSelectChange}
              placeholder="Select Vehicle"
              isClearable
              styles={selectStyles}
            />
          </div>

          {/* =====================================================
              HUB
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Hub</b>
            </label>

            <Select
              name="id_hub"
              options={hubOptions}
              value={
                hubOptions.find((option) => option.value === formData.id_hub) ||
                null
              }
              onChange={handleSelectChange}
              placeholder="Select Hub"
              isClearable
              styles={selectStyles}
            />
          </div>

          {/* =====================================================
              ASSIGN FROM
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Assign From *</b>
            </label>

            <input
              type="date"
              name="assignFromDate"
              value={formData.assignFromDate}
              onChange={handleChange}
              className="form-control"
            />

            {errors.assignFromDate && (
              <div className="text-danger mt-1">{errors.assignFromDate}</div>
            )}
          </div>

          {/* =====================================================
              ASSIGN TO
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Assign To</b>
            </label>

            <input
              type="date"
              name="assignToDate"
              value={formData.assignToDate}
              onChange={handleChange}
              className="form-control"
            />

            {errors.assignToDate && (
              <div className="text-danger mt-1">{errors.assignToDate}</div>
            )}
          </div>

          {/* =====================================================
              QUANTITY
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Quantity *</b>
            </label>

            <input
              type="number"
              name="quantity"
              min="1"
              value={formData.quantity}
              onChange={handleChange}
              className="form-control"
            />

            {errors.quantity && (
              <div className="text-danger mt-1">{errors.quantity}</div>
            )}
          </div>

          {/* =====================================================
              APPROVED
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label d-block">
              <b>Approved</b>
            </label>

            <div className="form-check form-switch mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="approved"
                checked={formData.approved}
                onChange={handleApprovedChange}
              />

              <label className="form-check-label" htmlFor="approved">
                {formData.approved ? "Yes" : "No"}
              </label>
            </div>
          </div>

          {/* =====================================================
              APPROVED BY
          ====================================================== */}

          <div className="col-md-6 mb-3">
            <label className="form-label">
              <b>Approved By</b>
            </label>

            <Select
              name="approvedBy"
              options={approvedByOptions}
              value={
                approvedByOptions.find(
                  (option) => option.value === formData.approvedBy,
                ) || null
              }
              onChange={handleSelectChange}
              placeholder="Select Approved By"
              isClearable
              isDisabled={!formData.approved}
              styles={selectStyles}
            />

            {errors.approvedBy && (
              <div className="text-danger mt-1">{errors.approvedBy}</div>
            )}
          </div>

          {/* =====================================================
              DESCRIPTION
          ====================================================== */}

          <div className="col-12 mb-3">
            <label className="form-label">
              <b>Description</b>
            </label>

            <textarea
              name="description"
              rows="4"
              placeholder="Enter Description"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>

        <Button variant="primary" onClick={handleUpdate} disabled={loading}>
          {loading ? "Updating..." : "Update Asset"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
